import { useState, type CSSProperties, type ReactNode } from 'react';
import { DropdownItem, type DropdownItemProps } from './DropdownItem';
import { DropdownFooter, type DropdownFooterProps } from './DropdownFooter';
import { useListNavigation } from '../../hooks/useListNavigation';
import { useReadOnly } from '../ReadOnly/ReadOnly';
import './Dropdown.css';

export type DropdownType = 'regular' | 'checkbox' | 'cascading' | 'flyout';

export type DropdownFlyoutPlacement = 'right' | 'left';

/** Dropdown list panel with selectable items. Supports regular single-select, checkbox multi-select, cascading (expandable sections — each item expands inline sub-items independently), and flyout (a parent item opens its `children` in a side submenu panel). */
export type DropdownProps = {
  /** `'regular'` for single-select list; `'checkbox'` for multi-select with checkboxes; `'cascading'` for expandable sections — clicking a parent item expands its `children` inline (multiple sections may be open at once); `'flyout'` for side submenus — hovering or clicking a parent item opens its `children` in an adjacent panel (one flyout open at a time). */
  type?: DropdownType;
  title?: string;
  showTitle?: boolean;
  showFooter?: boolean;
  /**
   * Custom footer content rendered below the item list. When provided, replaces
   * the default Clear/Apply `DropdownFooter` (even if `showFooter` is true).
   * Use for in-panel actions such as "create new …" inputs.
   */
  footer?: ReactNode;
  showPrefixIcon?: boolean;
  showInfoIcon?: boolean;
  /** For `type='cascading'` / `type='flyout'`, each item may include a `children` array shown inline (cascading) or in a side panel (flyout, with an optional `childrenTitle` header). Items with `type: 'section'` render as non-interactive group headers between rows. */
  items?: DropdownItemProps[];
  /** When `true` and `type='cascading'`, all parent items with children start expanded. Users can still collapse each section independently. */
  defaultExpanded?: boolean;
  /** For `type='flyout'`, which side the submenu panel opens on. Use `'left'` for menus anchored near the right screen edge. Defaults to `'right'`. */
  flyoutPlacement?: DropdownFlyoutPlacement;
  /** Selected item label (single-select) or array of labels (checkbox type). */
  value?: string | string[];
  /** Fired when an item is clicked. */
  onSelect?: (item: DropdownItemProps, index: number) => void;
  /** Fired when a cascading parent item is expanded or collapsed. */
  onExpand?: (item: DropdownItemProps, index: number, open: boolean) => void;
  /**
   * How many rows fit in the panel before the list scrolls. Defaults to 5
   * (Figma "Scroll-bar for long lists"). Pass the item count for short
   * action menus that should show every option without a scrollbar.
   */
  maxVisibleItems?: number;
  className?: string;
} & Pick<
  DropdownFooterProps,
  'clearLabel' | 'applyLabel' | 'onClear' | 'onApply' | 'clearDisabled' | 'applyDisabled'
>;

const MAX_VISIBLE_ITEMS = 5;
/** DOM id prefix for item rows; DropdownMenu uses it for `aria-activedescendant`. */
export const MENU_ID_PREFIX = 'spark-dropdown-item-';

function getInitialExpandedIndices(items: DropdownItemProps[], defaultExpanded: boolean): Set<number> {
  if (!defaultExpanded) return new Set();
  return new Set(
    items
      .map((item, index) =>
        Array.isArray(item.children) && item.children.length > 0 ? index : -1,
      )
      .filter((index) => index >= 0),
  );
}

export function Dropdown({
  type = 'regular',
  title = 'Title',
  showTitle = false,
  showFooter = false,
  footer,
  showPrefixIcon = false,
  showInfoIcon = false,
  items = [],
  defaultExpanded = false,
  flyoutPlacement = 'right',
  value,
  onSelect,
  onExpand,
  maxVisibleItems = MAX_VISIBLE_ITEMS,
  className = '',
  clearLabel,
  applyLabel,
  onClear,
  onApply,
  clearDisabled,
  applyDisabled,
}: DropdownProps) {
  const { readOnly } = useReadOnly();
  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(() =>
    getInitialExpandedIndices(items, defaultExpanded),
  );
  // Flyout submenus open one at a time (hover or click on the parent row).
  const [openFlyoutIndex, setOpenFlyoutIndex] = useState<number | null>(null);

  const { activeIndex, handleKeyDown } = useListNavigation({
    itemCount: items.length,
  });

  const isCascading = type === 'cascading';
  const isFlyout = type === 'flyout';
  const isScrollable = items.length > maxVisibleItems;
  const hasFooter = Boolean(footer) || showFooter;
  const rootClasses = [
    'spark-dropdown',
    `spark-dropdown-${type}`,
    isScrollable ? 'spark-dropdown-scrollable' : '',
    hasFooter ? 'spark-dropdown-with-footer' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    handleKeyDown(e);
    if ((e.key === 'Enter' || e.key === ' ') && activeIndex >= 0 && activeIndex < items.length) {
      e.preventDefault();
      const item = items[activeIndex];
      // Section headers are not selectable.
      if (item.type === 'section') return;
      if (item.state === 'disabled' || item.visualState === 'disabled' || (readOnly && item.disableWhenReadOnly)) return;
      // Flyout parents toggle their submenu instead of being selected.
      if (isFlyout && Array.isArray(item.children) && item.children.length > 0) {
        setOpenFlyoutIndex((prev) => (prev === activeIndex ? null : activeIndex));
      } else {
        onSelect?.(item, activeIndex);
      }
    }
  };

  const resolveItemState = (item: DropdownItemProps, index: number): DropdownItemProps => {
    const enriched: DropdownItemProps = { ...item };
    if (type === 'regular' && value !== undefined) {
      const selectedLabel = Array.isArray(value) ? value[0] : value;
      if (item.label === selectedLabel && !item.state) {
        enriched.state = 'selected';
      }
    }
    if (index === activeIndex && !enriched.visualState) {
      enriched.visualState = 'hover';
    }
    return enriched;
  };

  const handleCascadeExpand = (item: DropdownItemProps, index: number) => {
    const nextOpen = !expandedIndices.has(index);
    setExpandedIndices((prev) => {
      const next = new Set(prev);
      if (nextOpen) {
        next.add(index);
      } else {
        next.delete(index);
      }
      return next;
    });
    item.onExpand?.();
    onExpand?.(item, index, nextOpen);
  };

  return (
    <div
      className={rootClasses}
      role="menu"
      onKeyDown={handleMenuKeyDown}
      aria-activedescendant={activeIndex >= 0 ? `${MENU_ID_PREFIX}${activeIndex}` : undefined}
      style={{ '--spark-dropdown-max-visible': maxVisibleItems } as CSSProperties}
    >
      {showTitle ? <div className="spark-dropdown-title">{title}</div> : null}
      <div className="spark-dropdown-body">
        {items.map((item, index) => {
          // Non-interactive group header between rows (e.g. "Versions").
          if (item.type === 'section') {
            return (
              <div
                key={`section-${index}`}
                className={['spark-dropdown-section-header', item.className]
                  .filter(Boolean)
                  .join(' ')}
                role="presentation"
              >
                {item.label}
              </div>
            );
          }

          const resolved = resolveItemState(item, index);
          const hasChildren = Array.isArray(item.children) && item.children.length > 0;
          const isOpen = expandedIndices.has(index);

          if (isFlyout && hasChildren) {
            const isFlyoutOpen = openFlyoutIndex === index;
            const toggleFlyout = () => setOpenFlyoutIndex(isFlyoutOpen ? null : index);
            return (
              <div
                key={`${item.label}-${index}`}
                className="spark-dropdown-flyout-group"
                // The panel is a DOM child spanning the hover gap, so moving
                // the pointer from the parent row into the submenu does not
                // fire this leave handler.
                onMouseEnter={() => setOpenFlyoutIndex(index)}
                onMouseLeave={() =>
                  setOpenFlyoutIndex((prev) => (prev === index ? null : prev))
                }
              >
                <DropdownItem
                  {...resolved}
                  id={`${MENU_ID_PREFIX}${index}`}
                  visualState={resolved.visualState ?? (isFlyoutOpen ? 'hover' : undefined)}
                  showPrefixIcon={resolved.showPrefixIcon ?? showPrefixIcon}
                  showInfoIcon={resolved.showInfoIcon ?? showInfoIcon}
                  showExpandIcon
                  onClick={toggleFlyout}
                  onExpand={toggleFlyout}
                />
                {isFlyoutOpen && (
                  <div
                    className={`spark-dropdown-flyout-panel spark-dropdown-flyout-panel-${flyoutPlacement}`}
                  >
                    <Dropdown
                      title={item.childrenTitle}
                      showTitle={Boolean(item.childrenTitle)}
                      items={item.children}
                      maxVisibleItems={item.children!.length}
                      showPrefixIcon={showPrefixIcon}
                      showInfoIcon={showInfoIcon}
                      onSelect={onSelect}
                    />
                  </div>
                )}
              </div>
            );
          }

          if (isCascading) {
            return (
              <div key={`${item.label}-${index}`} className="spark-dropdown-cascade-group">
                <DropdownItem
                  {...resolved}
                  id={`${MENU_ID_PREFIX}${index}`}
                  showPrefixIcon={resolved.showPrefixIcon ?? showPrefixIcon}
                  showInfoIcon={resolved.showInfoIcon ?? showInfoIcon}
                  showExpandIcon={hasChildren}
                  expandIconOpen={isOpen}
                  onClick={() => {
                    if (hasChildren) {
                      handleCascadeExpand(item, index);
                    } else {
                      item.onClick?.();
                      onSelect?.(item, index);
                    }
                  }}
                  onExpand={() => handleCascadeExpand(item, index)}
                />
                {hasChildren && isOpen && (
                  <div className="spark-dropdown-cascade-children" role="group">
                    {item.children!.map((child, childIndex) => (
                      <DropdownItem
                        key={`${child.label}-${childIndex}`}
                        {...child}
                        showPrefixIcon={child.showPrefixIcon ?? showPrefixIcon}
                        showInfoIcon={child.showInfoIcon ?? showInfoIcon}
                        onClick={() => {
                          child.onClick?.();
                          onSelect?.(child, childIndex);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <DropdownItem
              key={`${item.label}-${index}`}
              {...resolved}
              id={`${MENU_ID_PREFIX}${index}`}
              type={type === 'checkbox' ? 'checkbox' : 'regular'}
              showPrefixIcon={resolved.showPrefixIcon ?? showPrefixIcon}
              showInfoIcon={resolved.showInfoIcon ?? showInfoIcon}
              onClick={() => {
                item.onClick?.();
                onSelect?.(item, index);
              }}
            />
          );
        })}
      </div>
      {hasFooter ? (
        footer != null ? (
          <div
            className="spark-dropdown-custom-footer"
            onMouseDown={(event) => event.stopPropagation()}
          >
            {footer}
          </div>
        ) : (
          <DropdownFooter
            clearLabel={clearLabel}
            applyLabel={applyLabel}
            onClear={onClear}
            onApply={onApply}
            clearDisabled={clearDisabled}
            applyDisabled={applyDisabled}
          />
        )
      ) : null}
    </div>
  );
}
